import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    
    // Initialize savings badge on load
    setTimeout(() => this.updateSavingsBadge(), 100);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
      
      // Update savings badge after price change
      setTimeout(() => this.updateSavingsBadge(), 50);
    }
  };

  /**
   * Updates the savings badge based on price and compare-at-price
   */
  updateSavingsBadge() {
    // Find the existing savings badge element (could be anywhere in the product section)
    const productSection = this.closest('.shopify-section, dialog') || document;
    const savingsBadgeContainer = productSection.querySelector('[class*="savings-badge-"]');
    const savingsBadgeText = productSection.querySelector('.saving-badge');

    if (!savingsBadgeContainer || !savingsBadgeText) {
      console.log('No existing savings badge found');
      return;
    }

    const priceElement = this.querySelector('.price');
    const compareAtPriceElement = this.querySelector('.compare-at-price');

    console.log('Price element text:', priceElement?.textContent);
    console.log('Compare-at-price element text:', compareAtPriceElement?.textContent);

    // If no compare-at-price or price, hide the badge container
    if (!priceElement || !compareAtPriceElement) {
      // Find the hidden class dynamically
      const hiddenClass = Array.from(savingsBadgeContainer.classList).find(cls => cls.includes('hidden'));
      if (hiddenClass) {
        savingsBadgeContainer.classList.add(hiddenClass);
      } else {
        savingsBadgeContainer.style.display = 'none';
      }
      console.log('No price elements found - hiding badge');
      return;
    }

    // Extract numeric values from the price strings (as regular numbers, not cents)
    const price = this.extractPrice(priceElement.textContent);
    const compareAtPrice = this.extractPrice(compareAtPriceElement.textContent);

    console.log('Extracted Price:', price);
    console.log('Extracted Compare-at-price:', compareAtPrice);

    // Calculate savings (difference between compare-at and price)
    const savings = compareAtPrice - price;

    console.log('Calculated Savings:', savings);

    // Only show badge if there are actual savings
    if (savings > 0) {
      // Get the prefix from the container's data attribute
      const prefix = savingsBadgeContainer.dataset.prefix || 'SAVE';
      
      // Format the savings amount with peso symbol and commas
      const formattedSavings = this.formatMoney(savings);
      
      console.log('Formatted Savings:', formattedSavings);
      
      // Update the badge text
      savingsBadgeText.textContent = prefix ? `${prefix} ${formattedSavings}` : formattedSavings;
      
      // Show the badge by removing hidden class
      const hiddenClass = Array.from(savingsBadgeContainer.classList).find(cls => cls.includes('hidden'));
      if (hiddenClass) {
        savingsBadgeContainer.classList.remove(hiddenClass);
      } else {
        savingsBadgeContainer.style.display = '';
      }
      
      console.log('Savings badge updated to:', savingsBadgeText.textContent);
    } else {
      // Hide the badge
      const hiddenClass = Array.from(savingsBadgeContainer.classList).find(cls => cls.includes('hidden'));
      if (hiddenClass) {
        savingsBadgeContainer.classList.add(hiddenClass);
      } else {
        savingsBadgeContainer.style.display = 'none';
      }
      console.log('No savings to show - hiding badge');
    }
  }

  /**
   * Extracts numeric price value from text
   * @param {string} priceText - The price text (e.g., "₱25,200", "₱5,000.00")
   * @returns {number} The numeric price value as a regular number
   */
  extractPrice(priceText) {
    if (!priceText) return 0;
    
    // Remove everything except digits, comma, and period
    // "₱25,200" becomes "25,200"
    let cleanedPrice = priceText.replace(/[^\d.,]/g, '');
    
    // Remove commas (Philippine format uses comma as thousands separator)
    // "25,200" becomes "25200"
    cleanedPrice = cleanedPrice.replace(/,/g, '');
    
    // Convert to number
    const price = parseFloat(cleanedPrice);
    
    return isNaN(price) ? 0 : price;
  }

  /**
   * Formats money with peso symbol and comma separators
   * @param {number} amount - Amount as a regular number (e.g., 300, 5000)
   * @returns {string} Formatted price string (e.g., "₱300", "₱5,000")
   */
  formatMoney(amount) {
    // Format the number with commas
    const formattedAmount = this.formatWithCommas(amount);
    
    // Return with peso symbol
    return `₱${formattedAmount}`;
  }

  /**
   * Formats number with comma separators
   * @param {number} amount - The amount to format
   * @returns {string} Formatted string with commas (e.g., "5,000" or "300")
   */
  formatWithCommas(amount) {
    // Round to remove decimals for whole peso amounts
    const roundedAmount = Math.round(amount);
    
    // Add comma separators
    return roundedAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}